import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { getBlogPosts } from '@/lib/contentful';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import MainLayout from '@/layouts/MainLayout';

export default function Blog() {
  const { data: posts, isLoading, error } = useQuery({
    queryKey: ['blog-posts'],
    queryFn: getBlogPosts,
  });

  if (error) {
    return (
      <MainLayout
        title="Error - Blog | Niumora"
        description="Error loading blog posts"
      >
        <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12">
          <h1 className="text-2xl font-bold mb-4">Error loading blog posts</h1>
          <p className="text-red-500">Please try again later.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      title="AI & Business Automation Blog | Niumora Insights"
      description="Stay updated with the latest insights, trends, and practical guides about AI implementation, business automation, and digital transformation. Expert articles from Niumora's AI specialists."
      keywords="AI blog, business automation, digital transformation, AI implementation, AI trends, business technology, Niumora insights"
      ogType="website"
    >
      <div className="container max-w-7xl mx-auto px-4 md:px-6 py-12 pt-24">
        <h1 className="text-4xl font-bold mb-8">Latest Articles</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <Card key={i} className="flex flex-col">
                  <CardHeader>
                    <Skeleton className="h-4 w-2/3" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-24 w-full" />
                  </CardContent>
                  <CardFooter>
                    <Skeleton className="h-4 w-1/3" />
                  </CardFooter>
                </Card>
              ))
            : posts?.map((post) => (
                <Link 
                  to={`/blog/${post.slug}`} 
                  className="block hover:opacity-90 transition-opacity"
                  key={post.slug}
                >
                  <Card className="flex flex-col h-full">
                    {post.featuredImage && (
                      <div className="relative h-48 overflow-hidden rounded-t-lg">
                        <img
                          src={post.featuredImage.fields.file.url}
                          alt={post.featuredImage.fields.title}
                          className="object-cover w-full h-full"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="line-clamp-2">
                        {post.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-grow">
                      <p className="text-muted-foreground line-clamp-3">{post.shortDescription}</p>
                    </CardContent>
                    <CardFooter className="flex justify-between items-center text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        {post.author?.fields && (
                          <span>{post.author.fields.name}</span>
                        )}
                      </div>
                      <time dateTime={post.publishedDate}>
                        {format(new Date(post.publishedDate), 'MMM d, yyyy')}
                      </time>
                    </CardFooter>
                  </Card>
                </Link>
              ))}
        </div>
      </div>
    </MainLayout>
  );
}
